import { useT } from '../../lib/i18n';
import { formatDateLong, formatDuration, formatTime, icsDataUrl, useMoney } from '../../lib/format';
import type { Booking } from '../../lib/types';

/**
 * One booking, as the customer sees it — used on the success screen and in
 * "my bookings". Actions are passed in rather than wired here so the success
 * screen can show a calendar link while the list shows cancel/reschedule.
 */
export function BookingCard({
  booking,
  actions,
  compact = false,
}: {
  booking: Booking;
  actions?: JSX.Element;
  compact?: boolean;
}): JSX.Element {
  const { t, lang } = useT();
  const money = useMoney();
  const tz = booking.timezone;
  // Statuses are tenant-configured and carry both names; fall back to the
  // English one when a tenant has not filled the Arabic label in.
  const statusLabel =
    lang === 'ar' ? (booking.status_name_ar ?? booking.status_name) : booking.status_name;

  return (
    <article className="bk-card overflow-hidden">
      <div className="flex items-start gap-3 p-4">
        <div
          className="flex w-14 shrink-0 flex-col items-center rounded-xl py-2"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
          aria-hidden
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide">
            {new Intl.DateTimeFormat(lang === 'ar' ? 'ar-EG' : 'en-GB', {
              month: 'short',
              timeZone: tz,
            }).format(new Date(booking.start_at))}
          </span>
          <span className="text-xl font-bold leading-tight">
            {new Intl.DateTimeFormat(lang === 'ar' ? 'ar-EG' : 'en-GB', {
              day: 'numeric',
              timeZone: tz,
            }).format(new Date(booking.start_at))}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="m-0 text-sm font-semibold text-zinc-900">
              {formatTime(booking.start_at, tz, lang)} – {formatTime(booking.end_at, tz, lang)}
            </p>
            {statusLabel && (
              <span
                className="bk-chip"
                style={{
                  background: `${booking.status_color ?? '#71717a'}1a`,
                  color: booking.status_color ?? '#3f3f46',
                }}
              >
                {statusLabel}
              </span>
            )}
            {booking.deposit_status === 'paid' && (
              <span className="bk-chip bg-emerald-50 text-emerald-700">{t('deposit_paid')}</span>
            )}
          </div>

          {!compact && (
            <p className="m-0 mt-0.5 text-xs text-zinc-500">{formatDateLong(booking.start_at, tz, lang)}</p>
          )}

          <p className="m-0 mt-2 truncate text-sm text-zinc-700">
            {booking.services.map((s) => s.name).join(' · ')}
          </p>

          <p className="m-0 mt-1 text-xs text-zinc-500">
            {[booking.branch_name, booking.employee_name].filter(Boolean).join(' · ')}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
            <span>{formatDuration(booking.duration_minutes, lang)}</span>
            <span className="font-semibold text-zinc-900">
              {money(booking.total)}
            </span>
            <span className="font-mono text-[11px] uppercase text-zinc-400">{booking.reference}</span>
          </div>
        </div>
      </div>

      {actions && <div className="flex flex-wrap gap-2 border-t border-zinc-100 px-4 py-3">{actions}</div>}
    </article>
  );
}

/** A data-URL `.ics` link, so "add to calendar" needs no server round trip. */
export function calendarHref(booking: Booking): string {
  return icsDataUrl({
    title: booking.services.map((s) => s.name).join(', ') || 'Appointment',
    start: booking.start_at,
    end: booking.end_at,
    location: booking.branch_name ?? '',
    description: `${booking.reference}${booking.employee_name ? ` · ${booking.employee_name}` : ''}`,
    uid: `${booking.reference}@matix-booking`,
  });
}
